import { createContext, useContext, useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getMgrSite, setMgrSite } from "@/lib/api";
import {
  LayoutDashboard, CalendarCheck, Clock, FileText, LogOut, Bell,
  CalendarDays, MapPin, ArrowLeft, BarChart3, KeyRound, TrendingUp, Fingerprint,
  AlertTriangle, FolderUp, Receipt, DollarSign, UtensilsCrossed, ShoppingCart,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { ManagePasskeysDialog } from "@/components/ManagePasskeysDialog";
import { SavePasskeyPrompt } from "@/components/SavePasskeyPrompt";

const SITE_LABEL = { SITE_1: "Christchurch", SITE_2: "Wellington", ALL: "All sites" };

const NAV = [
  { to: "/admin/manager/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/manager/roster", label: "Roster", icon: CalendarDays },
  { to: "/admin/manager/leave", label: "Leave", icon: CalendarCheck },
  { to: "/admin/manager/timesheets", label: "Timesheets", icon: Clock },
  { to: "/admin/manager/sales", label: "Sales", icon: DollarSign },
  { to: "/admin/manager/variance", label: "Variance", icon: TrendingUp },
  { to: "/admin/manager/trials", label: "90-day trials", icon: CalendarCheck },
  { to: "/admin/manager/reports", label: "Reports", icon: BarChart3 },
  { to: "/admin/manager/incidents", label: "Employee Incidents", icon: AlertTriangle },
  { to: "/admin/manager/site-incidents", label: "Site Incident Report", icon: AlertTriangle },
  { to: "/admin/manager/documents", label: "Upload Documents", icon: FolderUp },
  { to: "/admin/manager/expenses", label: "Expenses", icon: Receipt },
  { to: "/admin/manager/wastage", label: "Food Wastage", icon: UtensilsCrossed },
  { to: "/admin/manager/ordering", label: "Ordering Sheet", icon: ShoppingCart },
  { to: "/admin/manager/reports/activity", label: "Activity", icon: FileText },
];

// Context exposes the current scope + a token that increments whenever the
// site changes — manager pages include the token in their useEffect deps to
// re-fetch automatically.
const ManagerScopeContext = createContext({ site: "ALL", isAdminView: false, scopeToken: 0 });
export const useManagerScope = () => useContext(ManagerScopeContext);

function SidebarLink({ to, label, icon: Icon }) {
  return (
    <NavLink
      to={to}
      end={to === "/admin/manager/dashboard"}
      className={({ isActive }) =>
        [
          "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition",
          isActive
            ? "bg-[#C9A84C]/20 text-[#1A2744]"
            : "text-white/70 hover:text-white hover:bg-white/5",
        ].join(" ")
      }
      data-testid={`mgr-nav-${label.toLowerCase()}`}
    >
      <Icon className="h-4 w-4" strokeWidth={1.8} />
      {label}
    </NavLink>
  );
}

export function ManagerLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminView = user?.role === "ADMIN" || user?.role === "SUPERADMIN";
  // Voluntary password change is available for admin/manager accounts via
  // the user dropdown. Employees authenticate by PIN so they don't see it.
  const isStaffPortalUser = user && user.role !== "EMPLOYEE";
  const [changePwOpen, setChangePwOpen] = useState(false);

  // For MANAGER: scope is always their own site (not user-changeable).
  // For ADMIN/SUPERADMIN: scope is picked via the header select, persisted
  // to localStorage, defaulting to "ALL".
  const [site, setSiteState] = useState(() => {
    if (!isAdminView && user?.primary_site) return user.primary_site;
    return getMgrSite();
  });
  const [scopeToken, setScopeToken] = useState(0);

  useEffect(() => {
    // Keep api.js in sync. When a non-admin logs in, lock to their site.
    if (!isAdminView && user?.primary_site) {
      setMgrSite(user.primary_site);
      setSiteState(user.primary_site);
    } else {
      setMgrSite(site);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, user?.primary_site]);

  const onSiteChange = (next) => {
    setMgrSite(next);
    setSiteState(next);
    setScopeToken((t) => t + 1);
  };

  const siteLabel = SITE_LABEL[site] || site;
  const headerTitle = isAdminView ? `Admin view · ${siteLabel}` : `Manager · ${siteLabel}`;
  const showRosterWarning = isAdminView && site === "ALL" && location.pathname.endsWith("/roster");

  return (
    <ManagerScopeContext.Provider value={{ site, isAdminView, scopeToken }}>
      <div className="min-h-screen bg-[#F5F6F8] flex" data-testid="manager-layout">
        {/* Sidebar (desktop) */}
        <aside className="hidden lg:flex lg:w-64 lg:flex-col bg-[#1A2744] text-white">
          <Link to={isAdminView ? "/admin" : "/admin/manager/dashboard"} className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
            <img src="/logo-icon.png" alt="" className="h-9 w-9 rounded-lg bg-white/10 p-1.5" />
            <div className="min-w-0">
              <div className="font-heading text-[15px] font-bold leading-tight truncate">Coffee Lovers Café</div>
              <div className="text-[10px] uppercase tracking-widest text-[#C9A84C] font-semibold mt-0.5">
                {isAdminView ? `Admin · ${siteLabel}` : `Manager · ${siteLabel}`}
              </div>
            </div>
          </Link>
          <nav className="flex-1 px-3 py-4 space-y-1">
            {NAV
              .filter((n) => n.to !== "/admin/manager/sales" || user?.can_view_sales || ["ADMIN", "SUPERADMIN"].includes(user?.role))
              .map((n) => <SidebarLink key={n.to} {...n} />)}
          </nav>
          <div className="px-3 py-4 border-t border-white/10 space-y-1">
            {isAdminView && (
              <NavLink
                to="/admin"
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-white/70 hover:bg-white/5 hover:text-white"
                data-testid="mgr-back-to-admin"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back to Admin Home
              </NavLink>
            )}
            <div className="px-3 pt-2 pb-2">
              <div className="text-xs font-medium text-white">{user?.name}</div>
              <div className="text-[10px] text-white/50 truncate">{user?.email}</div>
            </div>
            <button
              onClick={async () => { await logout(); navigate("/admin/login", { replace: true }); }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-white/70 hover:bg-white/5 hover:text-white"
              data-testid="mgr-logout"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[#C9A84C] font-semibold">
                Coffee Lovers Café · {siteLabel}
              </div>
              <div className="text-sm font-medium text-[#1A2744] mt-0.5 truncate">{user?.name} · {headerTitle}</div>
            </div>
            <div className="flex items-center gap-3">
              {isAdminView && (
                <div className="flex items-center gap-2" data-testid="mgr-site-filter">
                  <MapPin className="h-4 w-4 text-[#1A2744]" />
                  <Select value={site} onValueChange={onSiteChange}>
                    <SelectTrigger className="w-[170px] h-9" data-testid="mgr-site-filter-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL" data-testid="mgr-site-filter-all">All sites</SelectItem>
                      <SelectItem value="SITE_1" data-testid="mgr-site-filter-chch">Christchurch</SelectItem>
                      <SelectItem value="SITE_2" data-testid="mgr-site-filter-wlg">Wellington</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <button className="relative p-2 rounded-lg hover:bg-gray-100" data-testid="mgr-bell">
                <Bell className="h-4 w-4 text-[#1A2744]" />
              </button>
              {/* User menu — visible on mobile + desktop so sign-out is always reachable */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="h-9 w-9 rounded-full bg-[#1A2744] text-white text-xs font-semibold flex items-center justify-center hover:bg-[#1A2744]/90"
                    data-testid="mgr-user-menu-trigger"
                    aria-label="User menu"
                  >
                    {(user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[220px]" data-testid="mgr-user-menu">
                  <DropdownMenuLabel className="font-normal">
                    <div className="text-sm font-semibold text-[#1A2744]">{user?.name}</div>
                    <div className="text-[11px] text-gray-500 truncate">{user?.email}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#C9A84C] font-semibold mt-1">
                      {user?.role} · {siteLabel}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {isAdminView && (
                    <DropdownMenuItem asChild data-testid="mgr-menu-admin-home">
                      <Link to="/admin" className="cursor-pointer">
                        <ArrowLeft className="h-3.5 w-3.5 mr-2" /> Back to Admin Home
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {isStaffPortalUser && (
                    <DropdownMenuItem
                      onClick={() => setChangePwOpen(true)}
                      data-testid="mgr-menu-change-password"
                      className="cursor-pointer"
                    >
                      <KeyRound className="h-3.5 w-3.5 mr-2" /> Change password
                    </DropdownMenuItem>
                  )}
                  <ManagePasskeysDialog
                    trigger={
                      <DropdownMenuItem
                        onSelect={(e) => e.preventDefault()}
                        data-testid="mgr-menu-passkeys"
                        className="cursor-pointer"
                      >
                        <Fingerprint className="h-3.5 w-3.5 mr-2" /> Passkeys
                      </DropdownMenuItem>
                    }
                  />
                  <DropdownMenuItem
                    onClick={async () => { await logout(); navigate("/admin/login", { replace: true }); }}
                    data-testid="mgr-menu-logout"
                    className="cursor-pointer text-red-600 focus:text-red-700"
                  >
                    <LogOut className="h-3.5 w-3.5 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {/* Content */}
          <main className="flex-1 px-4 py-6 sm:px-8 sm:py-8 pb-24 lg:pb-8" data-testid="mgr-main">
            {showRosterWarning && (
              <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs">
                Roster is per-site. Pick <span className="font-semibold">Christchurch</span> or <span className="font-semibold">Wellington</span> from the site filter to view or edit a roster.
              </div>
            )}
            {children}
          </main>

          {/* Bottom tab bar (mobile) */}
          <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-[#1A2744] text-white border-t border-white/10 flex items-stretch z-20">
            {NAV
              .filter((n) => n.to !== "/admin/manager/sales" || user?.can_view_sales || ["ADMIN", "SUPERADMIN"].includes(user?.role))
              .map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/admin/manager/dashboard"}
                className={({ isActive }) =>
                  [
                    "flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium",
                    isActive ? "text-[#C9A84C]" : "text-white/60",
                  ].join(" ")
                }
                data-testid={`mgr-tab-${n.label.toLowerCase()}`}
              >
                <n.icon className="h-4 w-4" strokeWidth={1.8} />
                {n.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      {/* Voluntary "Change password" entry — opened from the manager
          dropdown (admin/manager only; employees authenticate via PIN). */}
      {isStaffPortalUser && (
        <ChangePasswordDialog
          open={changePwOpen}
          onClose={() => setChangePwOpen(false)}
          forced={false}
        />
      )}

      {/* One-time "save passkey" prompt — auto-dismisses after first decision. */}
      <SavePasskeyPrompt />
    </ManagerScopeContext.Provider>
  );
}

export { SITE_LABEL as MANAGER_SITE_LABEL };
